"use client";

import { ReactLenis } from "lenis/react";
import { useSyncExternalStore, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { useReducedMotion } from "@/lib/motion";

/* Lenis-driven smooth page scroll.
 * Gated on prefers-reduced-motion: when the user opts out, we render
 * children with native scroll — no wrapper, no listeners, no cost.
 *
 * Also gated on /admin/* — admin needs the fastest possible scroll,
 * not interpolated. SOP: "for the admin page no animation no thing
 * so speed should boost up."
 *
 * Options tuned to the brand's motion spec (subtle, not flashy):
 *  - lerp 0.1 → unhurried but tracking
 *  - exponential ease-out (Lenis default, matches our easing aesthetic)
 *  - smoothWheel only (touch devices stay native, where it should)
 */
/* Phones and tablets keep native scroll. Lenis only ever smooths the
 * wheel, so on a touch device it was pure cost: the library, a
 * requestAnimationFrame loop and layout reads on every frame. Decided
 * after hydration (server snapshot = no Lenis) so the markup never
 * differs. Lenis is rendered as a SIBLING of the page, never as its wrapper: a
 * wrapper changes the element type at the top of the tree when it switches on,
 * and React then rebuilds the whole page underneath it. On the homepage that
 * rebuild put the main content BELOW the footer (fixed 2026-09-26). */
const subscribeFine = (cb: () => void) => {
  const mq = window.matchMedia("(pointer: fine)");
  mq.addEventListener("change", cb);
  return () => mq.removeEventListener("change", cb);
};
const getFine = () => window.matchMedia("(pointer: fine)").matches;
const getFineServer = () => false;

export function SmoothScroll({ children }: { children: ReactNode }) {
  const rm = useReducedMotion();
  const pathname = usePathname();
  const fine = useSyncExternalStore(subscribeFine, getFine, getFineServer);
  const smooth = !(rm || !fine || pathname?.startsWith("/admin"));

  return (
    <>
      {smooth ? (
        <ReactLenis
          root
          options={{
            lerp: 0.1,
            duration: 1.2,
            smoothWheel: true,
            easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          }}
        />
      ) : null}
      {children}
    </>
  );
}
